import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import { featureFlagsAPI, veBulkImportAPI } from '../services/api';
import { isPrivilegedStaffRole } from '../utils/permissions';

const STAGING_FLAG = 'data.staging.enabled';

const MASTER_FILES = [
  { key: 'course_categories_master', label: 'course_categories_master.csv' },
  { key: 'courses_master', label: 'courses_master.csv' },
  { key: 'batches_master', label: 'batches_master.csv' },
  { key: 'domains_master', label: 'domains_master.csv' },
  { key: 'companies_master', label: 'companies_master.csv' },
  { key: 'counselors_master', label: 'counselors_master.csv' },
];

const POLL_INTERVAL_MS = 3000;

const AdminVeBulkImportPage = () => {
  const { user } = useContext(AuthContext);
  const userRole = user?.role || user?.user?.role;
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('upsert');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [showAdvancedCsv, setShowAdvancedCsv] = useState(false);
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingComplete, setExportingComplete] = useState(false);
  const [job, setJob] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [stagingEnabled, setStagingEnabled] = useState(false);

  const pollRef = useRef(null);

  const refreshPreview = useCallback(async () => {
    try {
      const res = await veBulkImportAPI.getPreview();
      setPreview(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await veBulkImportAPI.getJob(jobId);
          setJob(res.data);
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            stopPolling();
            setSubmitting(false);
            refreshPreview();
          }
        } catch (err) {
          if (err.response?.status === 404) {
            stopPolling();
            setSubmitting(false);
            setErrorMessage('Import job expired or was lost. Check the database counts to verify results.');
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [refreshPreview, stopPolling]
  );

  useEffect(() => {
    if (!isPrivilegedStaffRole(userRole)) {
      navigate('/dashboard');
      return undefined;
    }
    refreshPreview();
    featureFlagsAPI
      .status([STAGING_FLAG])
      .then((res) => setStagingEnabled(Boolean(res.data?.data?.enabled?.[STAGING_FLAG])))
      .catch(() => setStagingEnabled(false));
    veBulkImportAPI
      .getActiveJob()
      .then((res) => {
        if (res.data.active && res.data.job) {
          setJob(res.data.job);
          setSubmitting(true);
          startPolling(res.data.job.id);
        }
      })
      .catch((err) => console.error(err));

    return () => stopPolling();
  }, [userRole, navigate, refreshPreview, startPolling, stopPolling]);

  const onFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file || undefined }));
  };

  const beginJob = async (apiCall) => {
    setErrorMessage(null);
    setJob(null);
    setSubmitting(true);
    try {
      const res = await apiCall();
      const jobId = res.data.jobId;
      if (res.data.job) setJob(res.data.job);
      startPolling(jobId);
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.activeJobId) {
        setErrorMessage(data.message);
        startPolling(data.activeJobId);
        return;
      }
      setSubmitting(false);
      if (error.response?.status === 413) {
        setErrorMessage(
          data?.message ||
            data?.error?.message ||
            'File is too large. VE Excel uploads must be under 50MB.'
        );
        return;
      }
      setErrorMessage(data?.message || data?.error?.message || data?.error || error.message || 'Import failed to start');
    }
  };

  const triggerXlsxDownload = (blobData, filename) => {
    const blob = new Blob([blobData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const readExportError = async (error) => {
    let message = error.message || 'Export failed';
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || message;
      } catch (_) {
        /* keep default */
      }
    } else if (data?.message) {
      message = data.message;
    }
    return message;
  };

  const handleExportExcel = async () => {
    setErrorMessage(null);
    setExporting(true);
    try {
      const res = await veBulkImportAPI.exportExcel();
      const stamp = new Date().toISOString().slice(0, 10);
      triggerXlsxDownload(res.data, `VE_Export_${stamp}.xlsx`);
    } catch (error) {
      setErrorMessage(await readExportError(error));
    } finally {
      setExporting(false);
    }
  };

  const handleExportComplete = async () => {
    setErrorMessage(null);
    setExportingComplete(true);
    try {
      const res = await veBulkImportAPI.exportExcelComplete();
      const stamp = new Date().toISOString().slice(0, 10);
      triggerXlsxDownload(res.data, `VE_Export_Complete_${stamp}.xlsx`);
    } catch (error) {
      setErrorMessage(await readExportError(error));
    } finally {
      setExportingComplete(false);
    }
  };

  const handleRunFromExcel = () => {
    if (!excelFile) {
      setErrorMessage('Please choose the VE Excel workbook (.xlsx)');
      return;
    }
    if (mode === 'replace_all' && !confirmReplace) {
      setErrorMessage('Check the box to confirm you want to delete all existing VE data');
      return;
    }
    const formData = new FormData();
    formData.append('excel', excelFile);
    formData.append('mode', mode);
    formData.append('confirm', mode === 'replace_all' ? 'true' : 'false');
    beginJob(() => veBulkImportAPI.runFromExcel(formData));
  };

  const handleRunFromCsv = () => {
    if (!files.students_full_upload) {
      setErrorMessage('students_full_upload.csv is required');
      return;
    }
    if (mode === 'replace_all' && !confirmReplace) {
      setErrorMessage('Check the box to confirm you want to delete all existing VE data');
      return;
    }
    const formData = new FormData();
    formData.append('mode', mode);
    formData.append('confirm', mode === 'replace_all' ? 'true' : 'false');
    Object.entries(files).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });
    beginJob(() => veBulkImportAPI.run(formData));
  };

  if (!isPrivilegedStaffRole(userRole)) return null;

  const running = submitting || job?.status === 'running';
  const report = job?.report;
  const phases = job?.phases || report?.phases || [];
  const parsePhase = phases.find((p) => p.phase === 'parse');
  const studentPhase = phases.find((p) => p.phase === 'students');
  const masterPhase = phases.find((p) => p.phase === 'masters');
  const resetPhase = phases.find((p) => p.phase === 'reset');
  const secondaryPhase = phases.find((p) => p.phase === 'secondary');

  return (
    <StaffPageShell
      title="Import VE Excel"
      subtitle="Upload the official VE Excel workbook. The system converts it and imports courses, batches, and students automatically."
    >
        <div className="profile-card theme-animate p-6">

          {stagingEnabled && (
            <div
              className="mb-6 p-3 rounded-lg text-sm border"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-primary-50)' }}
            >
              <strong>Data staging is on.</strong> Student rows go through staging: complete
              rows auto-apply to SQL; incomplete or ambiguous rows go to{' '}
              <Link to="/admin/data-staging-review" className="underline font-medium">
                Data staging review
              </Link>
              . Works with Replace all and Upsert. Replace all clears the staging review queue
              first, then wipes students/courses and imports. Masters still import directly.
            </div>
          )}

          <div
            className="mb-6 p-4 rounded-lg border flex flex-wrap items-center justify-between gap-3"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <div>
              <h2 className="font-semibold">Download VE Excel</h2>
              <p className="text-sm theme-text-secondary mt-1">
                Export current student data in the same multi-sheet format as{' '}
                <code>Final_VE_*.xlsx</code> (Personal, Mock, Readiness, Counseling, Placement, etc.).
                Rows are sorted by student name (enrollments oldest→newest within a student). Enrollment
                and batch dates are written as stored — sorting only changes row order. Students with
                multiple enrollments get one row per enrollment, highlighted in amber (lighter = newest /
                primary, deeper = older enrollments).
              </p>
              <p className="text-sm theme-text-secondary mt-2">
                <strong>Download complete</strong> also includes staging rows that are not already in SQL
                (name-only, unmerged duplicates, review queue)
                {preview?.counts?.stagingDocuments != null
                  ? ` — ${preview.counts.stagingDocuments} staging documents currently stored`
                  : ''}
                . Applied staging rows are represented by their SQL student, not duplicated.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <ThemeButton
                type="button"
                onClick={handleExportExcel}
                disabled={exporting || exportingComplete || running}
              >
                {exporting ? 'Preparing Excel…' : 'Download VE Excel'}
              </ThemeButton>
              <ThemeButton
                type="button"
                variant="outline"
                onClick={handleExportComplete}
                disabled={exporting || exportingComplete || running}
              >
                {exportingComplete ? 'Preparing complete…' : 'Download complete (SQL + staging)'}
              </ThemeButton>
            </div>
          </div>

          {preview?.counts && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-primary-50)' }}>
              <h2 className="font-semibold mb-2">Current database</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {Object.entries(preview.counts).map(([k, v]) => (
                  <div key={k}>
                    <span className="opacity-70">{k}:</span> <strong>{v}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-error-50, #fef2f2)', color: '#b91c1c' }}>
              {errorMessage}
            </div>
          )}

          <ImportModeSection
            mode={mode}
            setMode={setMode}
            confirmReplace={confirmReplace}
            setConfirmReplace={setConfirmReplace}
            disabled={running}
          />

          <div className="mb-6 p-4 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
            <h2 className="font-semibold mb-2">Step 1 — Upload Excel workbook</h2>
            <p className="text-xs theme-text-secondary mb-3">
              Use the same file you receive from VE (e.g. Final_VE_*.xlsx). A full workbook may take
              5–15 minutes to import.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={running}
              onChange={(e) => setExcelFile(e.target.files[0] || null)}
              className="block w-full text-sm mb-4"
            />
            <ThemeButton
              onClick={handleRunFromExcel}
              disabled={running}
              className="w-full py-3"
              style={{
                backgroundColor: running ? 'var(--color-neutral-400)' : 'var(--color-warning)',
                color: 'white',
              }}
            >
              {running ? 'Import in progress…' : 'Step 2 — Run full import from Excel'}
            </ThemeButton>
          </div>

          <details
            className="mb-6"
            open={showAdvancedCsv}
            onToggle={(e) => setShowAdvancedCsv(e.target.open)}
          >
            <summary className="cursor-pointer text-sm font-medium opacity-80">
              Advanced: import from CSV files instead
            </summary>
            <div className="mt-4 space-y-4 pl-2 border-l-2 ml-1" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-xs opacity-70">
                Only if you already ran the parser elsewhere and have the CSV bundle.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1">
                  students_full_upload.csv <span className="text-red-600">*</span>
                </label>
                <input
                  type="file"
                  accept=".csv"
                  disabled={running}
                  onChange={(e) => onFile('students_full_upload', e.target.files[0])}
                  className="block w-full text-sm"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {MASTER_FILES.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs mb-1">{label}</label>
                    <input
                      type="file"
                      accept=".csv"
                      disabled={running}
                      onChange={(e) => onFile(key, e.target.files[0])}
                      className="block w-full text-xs"
                    />
                  </div>
                ))}
              </div>
              <ThemeButton
                onClick={handleRunFromCsv}
                disabled={running}
                className="w-full py-2 text-sm"
                style={{
                  backgroundColor: running ? 'var(--color-neutral-400)' : 'var(--color-neutral-600)',
                  color: 'white',
                }}
              >
                {running ? 'Import in progress…' : 'Run import from CSV files'}
              </ThemeButton>
            </div>
          </details>

          {job && (
            <ImportProgress
              job={job}
              report={report}
              parsePhase={parsePhase}
              resetPhase={resetPhase}
              masterPhase={masterPhase}
              studentPhase={studentPhase}
              secondaryPhase={secondaryPhase}
            />
          )}
        </div>
    </StaffPageShell>
  );
};

function ImportModeSection({ mode, setMode, confirmReplace, setConfirmReplace, disabled }) {
  return (
    <div className="mb-6 space-y-3">
      <h2 className="font-semibold">Import mode</h2>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="mode"
          disabled={disabled}
          checked={mode === 'upsert'}
          onChange={() => setMode('upsert')}
        />
        <span>Upsert — keep existing data, update from file</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="mode"
          disabled={disabled}
          checked={mode === 'replace_all'}
          onChange={() => setMode('replace_all')}
        />
        <span className="text-red-700 font-medium">
          Replace all — delete students, enrollments, batches, courses, and clear staging, then import
        </span>
      </label>
      {mode === 'replace_all' && (
        <label className="flex items-center gap-2 ml-6 text-sm text-red-800">
          <input
            type="checkbox"
            disabled={disabled}
            checked={confirmReplace}
            onChange={(e) => setConfirmReplace(e.target.checked)}
          />
          I understand this will permanently remove student/course data and clear the staging review queue
        </label>
      )}
    </div>
  );
}

function StatusBanner({ job }) {
  const map = {
    running: { text: 'Import running… keep this page open (safe to refresh)', bg: '#fef9c3', fg: '#854d0e' },
    completed: { text: '✅ Import completed', bg: '#dcfce7', fg: '#166534' },
    failed: { text: '❌ Import failed', bg: '#fee2e2', fg: '#b91c1c' },
  };
  const s = map[job.status] || map.running;
  return (
    <div className="p-3 rounded-lg text-sm font-medium flex items-center gap-2" style={{ backgroundColor: s.bg, color: s.fg }}>
      {job.status === 'running' && <Spinner />}
      <span>{s.text}</span>
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
      aria-hidden="true"
    />
  );
}

function PhaseStatusRow({ label, phase }) {
  const status = phase?.status || 'pending';
  const icon =
    status === 'completed' ? '✅' : status === 'failed' ? '❌' : status === 'running' ? '⏳' : status === 'skipped' ? '⏭️' : '⬜';
  return (
    <div className="flex items-center gap-2 text-sm">
      <span>{icon}</span>
      <span className="font-medium">{label}</span>
      {phase?.message && <span className="opacity-70">— {phase.message}</span>}
    </div>
  );
}

function ImportProgress({ job, report, parsePhase, resetPhase, masterPhase, studentPhase, secondaryPhase }) {
  const isExcel = job.kind === 'excel';
  return (
    <div className="space-y-4 border-t pt-6">
      <StatusBanner job={job} />

      <div className="p-4 rounded-lg border space-y-1" style={{ borderColor: 'var(--color-border)' }}>
        <h3 className="font-semibold mb-2">Progress</h3>
        {isExcel && <PhaseStatusRow label="Convert Excel" phase={parsePhase} />}
        <PhaseStatusRow label="Reset data" phase={resetPhase} />
        <PhaseStatusRow label="Import masters" phase={masterPhase} />
        <PhaseStatusRow label="Import students" phase={studentPhase} />
        <PhaseStatusRow label="Import readiness / mocks / concerns / opps" phase={secondaryPhase} />
      </div>

      {job.error && (
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}>
          {job.error}
        </div>
      )}

      {report?.message && <p className="text-sm">{report.message}</p>}

      {parsePhase && <ParsePhaseCard phase={parsePhase} />}
      {resetPhase && resetPhase.status !== 'pending' && <PhaseCard title="Reset" phase={resetPhase} />}
      {masterPhase && masterPhase.status !== 'pending' && <PhaseCard title="Masters" phase={masterPhase} />}
      {studentPhase && studentPhase.status !== 'pending' && (
        <PhaseCard title="Students" phase={studentPhase} showErrors showSuccess />
      )}
      {secondaryPhase && secondaryPhase.status !== 'pending' && secondaryPhase.status !== 'skipped' && (
        <SecondaryPhaseCard phase={secondaryPhase} />
      )}

      {report?.afterCounts && (
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-success-50)' }}>
          <h3 className="font-semibold mb-2">After import</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {Object.entries(report.afterCounts).map(([k, v]) => (
              <div key={k}>
                {k}: <strong>{v}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ParsePhaseCard({ phase }) {
  if (!phase.stats && !phase.logExcerpt) return null;
  return (
    <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
      <h3 className="font-semibold mb-2">
        Excel conversion — <span className="capitalize">{phase.status}</span>
      </h3>
      {phase.stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-2">
          {Object.entries(phase.stats).map(([name, s]) => (
            <div key={name}>
              <span className="opacity-70">{name}:</span>{' '}
              <strong>{s.present ? `${s.dataRows} rows` : 'missing'}</strong>
            </div>
          ))}
        </div>
      )}
      {phase.logExcerpt && (
        <details>
          <summary className="cursor-pointer text-xs">Parser log (last lines)</summary>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32 mt-1 whitespace-pre-wrap">
            {phase.logExcerpt}
          </pre>
        </details>
      )}
    </div>
  );
}

function SecondaryPhaseCard({ phase }) {
  return (
    <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
      <h3 className="font-semibold mb-2">
        Secondary data — <span className="capitalize">{phase.status?.replace(/_/g, ' ')}</span>
      </h3>
      {phase.message && <p className="text-sm mb-2">{phase.message}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-2">
        <Stat label="Total imported" value={phase.totalImported} />
        <Stat label="Skipped (no enrollment)" value={phase.totalSkipped} />
        <Stat label="Interview readiness" value={phase.interviewReadiness?.imported} />
        <Stat label="Mock interviews" value={phase.mockInterviews?.imported} />
        <Stat label="Training concerns" value={phase.trainingConcerns?.imported} />
        <Stat label="Placement opps" value={phase.placementOpportunities?.imported} />
      </div>
      {phase.errors?.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-red-700">
            Secondary import errors ({phase.errors.length})
          </summary>
          <pre className="mt-2 text-xs overflow-auto max-h-40 p-2 rounded bg-red-50">
            {JSON.stringify(phase.errors.slice(0, 20), null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function PhaseCard({ title, phase, showErrors, showSuccess }) {
  return (
    <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
      <h3 className="font-semibold mb-2">
        {title} — <span className="capitalize">{phase.status}</span>
      </h3>
      {phase.message && <p className="text-sm mb-2">{phase.message}</p>}
      {phase.viaStaging && (
        <p className="text-sm mb-2">
          Via data staging
          {phase.reviewQueueCount != null ? ` — ${phase.reviewQueueCount} need review. ` : '. '}
          <Link to="/admin/data-staging-review" className="underline font-medium">
            Open review queue
          </Link>
        </p>
      )}
      {phase.results && (
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
          {JSON.stringify(phase.results, null, 2)}
        </pre>
      )}
      {phase.warnings?.length > 0 && (
        <ul className="text-sm text-amber-800 list-disc pl-5 mt-2">
          {phase.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      {phase.totalRows != null && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
          <Stat label="Total rows" value={phase.totalRows} />
          <Stat label="Success" value={phase.successCount} />
          <Stat label="Created" value={phase.createdCount} />
          <Stat label="Updated" value={phase.updatedCount} />
          <Stat label="With enrollment" value={phase.enrolledCount} />
          <Stat label="No enrollment" value={phase.withoutEnrollmentCount} />
          <Stat label="Errors" value={phase.errorCount} />
        </div>
      )}
      {showErrors && phase.errorDetails?.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium text-red-700">
            Failed rows ({phase.errorDetails.length})
          </summary>
          <div className="max-h-48 overflow-y-auto mt-2 space-y-1">
            {phase.errorDetails.slice(0, 100).map((e, i) => (
              <div key={i} className="text-xs p-2 bg-red-50 rounded">
                <strong>Row {e.rowNumber}</strong> {e.email && `— ${e.email}`}
                <br />
                {e.error}
              </div>
            ))}
            {phase.errorDetails.length > 100 && (
              <p className="text-xs">…and {phase.errorDetails.length - 100} more</p>
            )}
          </div>
        </details>
      )}
      {showSuccess && phase.successDetails?.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium text-green-700">
            Uploaded rows sample ({phase.successDetails.length} total)
          </summary>
          <div className="max-h-32 overflow-y-auto mt-2 text-xs">
            {phase.successDetails.slice(0, 20).map((s, i) => (
              <div key={i}>
                {s.email} — {s.action}
                {s.enrollmentLinked ? ' (enrolled)' : ' (no enrollment)'}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="opacity-70">{label}</div>
      <div className="font-bold">{value ?? '—'}</div>
    </div>
  );
}

export default AdminVeBulkImportPage;
